import { Info, Plus, X } from 'lucide-react';
import { newUuid } from '@shared';
import { useDispatch, useSelector } from 'react-redux';
import {
  putAdvisorReferralSourceByAdvisorIdRequest,
  selectAdvisorUpdatesStatus,
  selectSessionAdvisor,
} from '@/model';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dispatch, SetStateAction, useState } from 'react';
import { cn } from '@/lib/utils';
import _ from 'lodash';
import { CustomTooltip, FieldLabel } from '@client';

interface SelectReferralSourceProps {
  selectedReferralSourceId: string | undefined;
  onChangeReferralSourceId: (value: string | undefined) => void;
  placeholderText?: string;
  triggerClassName?: string;
  selectValueClassName?: string;
  disabled?: boolean;
  advisor?: Advisor;
  addInlineOption?: boolean;
  client?: Client;
  includeFieldLabel?: boolean;
  controlledReferralSources?: AdvisorReferralSource[];
  setControlledReferralSources?: Dispatch<SetStateAction<AdvisorReferralSource[]>>;
}

export function SelectReferralSource({
  selectedReferralSourceId,
  onChangeReferralSourceId,
  placeholderText = 'Undefined',
  triggerClassName,
  selectValueClassName,
  disabled,
  advisor,
  addInlineOption = false,
  client,
  includeFieldLabel = true,
  controlledReferralSources,
  setControlledReferralSources,
}: SelectReferralSourceProps) {
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const status = useSelector(selectAdvisorUpdatesStatus).putAdvisorReferralSource;
  const isLoading = status === 'loading';
  const selectedAdvisor = advisor ?? sessionAdvisor;
  const [isAddingMode, setIsAddingMode] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const selectedReferralSource = selectedAdvisor.referralSources?.find(
    source => source.advisorReferralSourceId === selectedReferralSourceId,
  );
  const [referralSources, _setReferralSources] = useState<AdvisorReferralSource[]>(
    _.sortBy(
      selectedAdvisor.referralSources?.map(source => ({
        referralSourceLabel: source.referralSourceLabel,
        advisorReferralSourceId: source.advisorReferralSourceId,
      })),
      source => source.referralSourceLabel.toLowerCase().trim(),
    ) ?? [],
  );

  const referralSourcesToUse = controlledReferralSources || referralSources;
  const setReferralSourcesToUse = setControlledReferralSources || _setReferralSources;

  const selectedReferralSourceLocal = referralSourcesToUse?.find(
    source => source.advisorReferralSourceId === selectedReferralSourceId,
  );

  const handleSetReferralSources = (sources: AdvisorReferralSource[]) => {
    setReferralSourcesToUse(
      _.sortBy(sources, source => source.referralSourceLabel.toLowerCase().trim()),
    );
  };

  const dispatch = useDispatch();

  const handleValueChange = (sourceId: string) => {
    if (sourceId === 'undefined') {
      onChangeReferralSourceId(undefined);
    } else {
      // selecting the same source, removes selection
      onChangeReferralSourceId(sourceId === selectedReferralSourceId ? undefined : sourceId);
    }
  };

  const handleAddSource = () => {
    if (!newSourceName.trim()) return;

    const existingSource = referralSourcesToUse.find(
      source => source.referralSourceLabel.toLowerCase() === newSourceName.toLowerCase(),
    );

    if (existingSource) {
      onChangeReferralSourceId(existingSource.advisorReferralSourceId);
      setIsAddingMode(false);
      setNewSourceName('');
      return;
    }

    const newSource = {
      referralSourceLabel: newSourceName,
      advisorReferralSourceId: newUuid(),
    } as AdvisorReferralSource;

    onChangeReferralSourceId(newSource.advisorReferralSourceId);

    if (!addInlineOption) {
      dispatch({ type: 'DEFER_SESSION_REFRESH' });
    }

    dispatch(
      putAdvisorReferralSourceByAdvisorIdRequest({
        advisorReferralSourceId: newSource.advisorReferralSourceId,
        referralSourceLabel: newSource.referralSourceLabel,
        advisorId: selectedAdvisor.advisorId,
        clientId: client?.clientId,
      }),
    );

    handleSetReferralSources([...referralSourcesToUse, newSource]);
    setIsAddingMode(false);
    setNewSourceName('');
  };

  const handleCancel = () => {
    setIsAddingMode(false);
    setNewSourceName('');
  };

  if (!addInlineOption && isAddingMode) {
    return (
      <div className="flex gap-2">
        <Input
          value={newSourceName}
          onChange={e => setNewSourceName(e.target.value)}
          placeholder="Enter new source..."
          className="w-full text-gray-700"
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              handleAddSource();
            }
            if (e.key === 'Escape') handleCancel();
          }}
          autoFocus
          disabled={isLoading}
          data-testid="referral-source-new-input"
        />
        <Button
          className="shrink-0"
          onClick={handleAddSource}
          disabled={!newSourceName.trim() || isLoading}
        >
          {isLoading ? 'Adding...' : 'Add'}
        </Button>
        <Button
          variant="secondary"
          size="icon"
          onClick={handleCancel}
          disabled={isLoading}
          className="shrink-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {includeFieldLabel && (
        <div className="flex items-baseline">
          <FieldLabel text="Referral Source" />
          <CustomTooltip
            content={
              <div className="text-sm font-normal">
                <p>Track how clients are finding you by selecting or creating a referral source</p>
              </div>
            }
          >
            <Info className="text-primary-500 ml-2 h-4 w-4" />
          </CustomTooltip>
        </div>
      )}
      <div className="flex gap-2">
        <Select
          onValueChange={handleValueChange}
          value={selectedReferralSourceId ?? 'undefined'}
        >
          <SelectTrigger
            className={cn('w-full text-gray-700', triggerClassName)}
            disabled={disabled || isLoading}
            data-testid="referral-source-trigger"
          >
            <SelectValue>
              <div className={cn('flex items-center gap-2 truncate', selectValueClassName)}>
                {selectedReferralSourceId
                  ? selectedReferralSource?.referralSourceLabel ||
                    selectedReferralSourceLocal?.referralSourceLabel
                  : placeholderText}
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                value="undefined"
                className="hover:bg-primary/10 focus:bg-primary/10 cursor-pointer"
                data-testid="referral-source-option-undefined"
              >
                {placeholderText}
              </SelectItem>
              {referralSourcesToUse.map(source => (
                <SelectItem
                  key={source.advisorReferralSourceId}
                  value={source.advisorReferralSourceId}
                  className="hover:bg-primary/10 focus:bg-primary/10 cursor-pointer"
                  data-testid={`referral-source-option-${source.referralSourceLabel}`}
                >
                  {source.referralSourceLabel}
                </SelectItem>
              ))}
              {addInlineOption && (
                <>
                  {isAddingMode ? (
                    <div
                      className="p-2"
                      data-testid="referral-source-add-form"
                    >
                      <Input
                        value={newSourceName}
                        onChange={e => setNewSourceName(e.target.value)}
                        placeholder="Enter new source..."
                        className="mb-2"
                        autoFocus
                        disabled={isLoading}
                        data-testid="referral-source-new-input"
                        onKeyDown={e => {
                          e.stopPropagation();
                          if (e.key === 'Enter') handleAddSource();
                          if (e.key === 'Escape') handleCancel();
                        }}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={handleCancel}
                          disabled={isLoading}
                          data-testid="referral-source-cancel-button"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleAddSource}
                          disabled={isLoading}
                          data-testid="referral-source-add-button"
                        >
                          {isLoading ? 'Adding...' : 'Add'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-left font-normal"
                      onClick={() => setIsAddingMode(true)}
                      data-testid="referral-source-add-new-button"
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      Add New Referral Source
                    </Button>
                  )}
                </>
              )}
            </SelectGroup>
          </SelectContent>
        </Select>
        {!addInlineOption && (
          <Button
            variant="outline"
            className="flex gap-2"
            onClick={() => setIsAddingMode(true)}
            disabled={disabled || isLoading}
            data-testid="referral-source-add-new-button"
          >
            <Plus className="h-4 w-4" />
            Add New
          </Button>
        )}
      </div>
    </div>
  );
}
